import React, { memo, useCallback, useMemo } from 'react';
import {
  Table as NexusTable,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableActionsCell,
  TableHeaderSortCell,
  IconButton as NexusIconButton,
  useNexus,
  Tag as NexusTag,
  ActionMenu,
  showToast,
  TableLoadingView,
  TableSortDirection,
} from '@spotoninc/nexus-react';
import {
  ManageRequestsIcon,
  MoreVerticalIcon,
} from '@spotoninc/nexus-web-assets';
import '../../../../styles/reskinned-task-manager.css';
import styles from './improvedTaskTable.module.css';

import capitalize from 'lodash/capitalize';

// Import SVG images needed for status tags
import completedSvg from '../../../../media/images/completed-status.svg';
import cancelledSvg from '../../../../media/images/cancelled-status.svg';
import inProgressSvg from '../../../../media/images/in-progress.svg';
import todoSvg from '../../../../media/images/todo.svg';
import {
  extractPriorityFromDescription,
  getPriorityVariant,
  getStatusLabel,
  processTaskValue,
} from '../tasks.utils';
import toLower from 'lodash/toLower';
import size from 'lodash/size';
import { Box, Flex } from '@chakra-ui/react';

const emptyTextMessage =
  'Hang tight! We’ll surface new opportunities\nas soon as they’re available.';
/**
 * ImprovedTaskTable component for displaying and managing tasks
 * Dumb component that receives data via props
 *
 * Parent
 *      ImprovedTaskManager
 */
function ImprovedTaskTable({
  tasks = [],
  sort,
  loading = false,
  handleDelete,
  handleEditClick,
  handleSort,
  onRowClick,
}) {
  useNexus(); // Initialize Nexus context

  // Function to get status tag with icon
  const getStatusTag = status => {
    let color;
    let icon;

    switch (status) {
      case 'completed':
        color = 'success';
        icon = completedSvg;
        break;
      case 'cancelled':
        color = 'warning';
        icon = cancelledSvg;
        break;
      case 'in-progress':
        color = 'info';
        icon = inProgressSvg;
        break;
      default:
        color = 'danger';
        icon = todoSvg;
    }

    return (
      <NexusTag color={color} style={{ padding: '4px 8px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <img
            src={icon}
            alt={status}
            style={{ width: '16px', height: '16px', verticalAlign: 'middle' }}
          />
          <span style={{ verticalAlign: 'middle' }}>
            {/* {status === 'Not yet Started' ? 'To Do' : status} */}
            {getStatusLabel(status)}
          </span>
        </div>
      </NexusTag>
    );
  };

  const renderActionMenu = useCallback(task => {
    const canCancel = task.status === 'todo' || task.status === 'in-progress';

    const options = [
      {
        label: 'Edit Task',
        onClick: () => handleEditClick(task),
      },
    ];

    if (canCancel) {
      options.push({
        label: 'Cancel Task',
        onClick: () => {
          try {
            handleDelete(task.id, task?.version, task?.task_title || '');
          } catch (error) {
            showToast({
              priority: 'error',
              content:
                'Failed to cancel task: ' + (error.message || 'Unknown error'),
              autoClose: 6000,
            });
          }
        },
      });
    }

    return (
      <ActionMenu options={options}>
        {({ getRootProps }) => (
          <NexusIconButton
            {...getRootProps()}
            icon={MoreVerticalIcon}
            label="More"
            variant="text"
            size="small"
          />
        )}
      </ActionMenu>
    );
  }, []);

  return (
    <div
      className={`nexus-task-table-container ${styles.mobileTableContainer}`}
      style={{
        position: 'relative',
      }}
    >
      <NexusTable
        className={styles.taskTable}
        style={{
          tableLayout: loading ? 'auto' : 'fixed',
          width: '100%',
          minWidth: '100%',
        }}
      >
        <TableHead className="nexus-task-table-header">
          <TableRow>
            <TableHeaderSortCell
              onSort={() => handleSort('due_on')}
              sortDirection={sort?.due_on || undefined}
              className={styles.dueDateColumn}
            >
              Due Date
            </TableHeaderSortCell>
            <TableHeaderCell
              onSort={() => {
                // reverse sort direction added because we need to show high on top
                handleSort('priority', TableSortDirection.descending);
              }}
              sortDirection={sort?.priority || undefined}
              className={styles.priorityColumn}
            >
              Priority
            </TableHeaderCell>
            <TableHeaderCell
              onSort={() => handleSort('assigned_to')}
              sortDirection={sort?.assigned_to || undefined}
              className={styles.assignedColumn}
            >
              Assigned to
            </TableHeaderCell>
            <TableHeaderCell
              onSort={() => handleSort('task_title')}
              sortDirection={sort?.task_title || undefined}
              className={styles.taskColumn}
            >
              Task
            </TableHeaderCell>
            <TableHeaderCell
              onSort={() => handleSort('status')}
              sortDirection={sort?.status || undefined}
              className={styles.statusColumn}
            >
              Status
            </TableHeaderCell>
            <TableHeaderCell className={styles.actionsColumn}>
              Actions
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody
          loadingView={
            loading ? (
              <TableLoadingView rows={20} variant="filled" />
            ) : undefined
          }
        >
          {!!size(tasks) ? (
            tasks.map(task => {
              const priority =
                task.version === '1'
                  ? toLower(extractPriorityFromDescription(task.task_desc))
                  : task?.priority;
              return (
                <TableRow
                  key={task.id}
                  onClick={() => onRowClick && onRowClick(task)}
                  style={{ cursor: 'pointer' }}
                  className="nexus-task-table-row"
                >
                  <TableCell
                    className={styles.dueDateColumn}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '1rem',
                    }}
                  >
                    {task.due_on || '--'}
                  </TableCell>
                  <TableCell
                    className={styles.priorityColumn}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {priority ? (
                      <NexusTag
                        variant={getPriorityVariant(priority)}
                        size="small"
                        className="nexus-priority-tag"
                      >
                        {capitalize(priority)}
                      </NexusTag>
                    ) : (
                      '--'
                    )}
                  </TableCell>
                  <TableCell
                    className={styles.assignedColumn}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '1rem',
                    }}
                  >
                    {task?.assignedUser?.first_name || '--'}
                  </TableCell>
                  <TableCell
                    className={styles.taskColumn}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: '1rem',
                    }}
                  >
                    {processTaskValue(task.task_title) || '--'}
                  </TableCell>
                  <TableCell
                    className={`nexus-task-table-status-cell ${styles.statusColumn}`}
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {task.status ? getStatusTag(task.status) : '--'}
                  </TableCell>
                  <TableActionsCell
                    className={styles.actionsColumn}
                    onClick={e => e.stopPropagation()} // Prevent row click when clicking on actions
                  >
                    {renderActionMenu(task)}
                  </TableActionsCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6}>
                <Flex justifyContent={'center'} p={6}>
                  <Flex
                    direction={'column'}
                    alignItems={'center'}
                    maxW={'362px'}
                    textAlign={'center'}
                    color={'var(--Text-neutral-strongest, #0A091B)'}
                    fontSize={'var(--font-size-100, 16px)'}
                    fontFamily={'var(--font-family-sans, Poppins)'}
                  >
                    <ManageRequestsIcon
                      size={'24px'}
                      color={'var(--Text-neutral-lighest, #37456C)'}
                    />
                    <Box pt={4}>{emptyTextMessage}</Box>
                  </Flex>
                </Flex>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </NexusTable>
    </div>
  );
}

export default memo(ImprovedTaskTable);
